import React, { useState } from "react";
import { BatteryCharging, MessageCircleQuestion } from "lucide-react";
import { Button } from "../ui/button";
import { describeWeek } from "../../utils/programmeWeeks";

// Light-week answers, in the order they're offered. Vertical, full-width
// buttons: the main choice on a phone, not a sideways pill row.
const LIGHT_ANSWERS = [
  { answer: "ill", label: "I was ill" },
  { answer: "travelling", label: "I was travelling" },
  { answer: "busy", label: "Busy or tired" },
  { answer: "fine", label: "It was fine" },
];

/**
 * The weekly check-in (#94), at the top of the Programme tab. Khronos spotted
 * a light week, or two hard ones, from the numbers; the user says what
 * happened and the programme responds. Nothing changes without a tap, and
 * anything that moves a deload comes with Undo (owned by the parent).
 *
 * `onLightAnswer(answer, { countAsDeload })` and `onHardAnswer(accept)` do the
 * work; this component only asks.
 */
const ProgrammeCheckInCard = ({ suggestion, onLightAnswer, onHardAnswer, busy = false }) => {
  // Second step: { answer, step: "deload" | "repeat" }. Travelling or busy
  // with a deload due soon asks about the deload; ill or travelling otherwise
  // offers to repeat the week (#92) when days were missed.
  const [pending, setPending] = useState(null);
  if (!suggestion) return null;

  if (suggestion.type === "hard") {
    return (
      <section
        aria-label="Weekly check-in"
        className="grid gap-3 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-4"
      >
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-yellow-500">
          <BatteryCharging className="h-3.5 w-3.5" /> Check-in
        </p>
        <div>
          <h3 className="text-lg font-bold leading-tight text-white">Two hard weeks in a row</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            Most of your sessions felt 9 or 10 out of 10.{" "}
            {suggestion.nextDeload
              ? `Take your lighter deload week now instead of in week ${suggestion.nextDeload}?`
              : "Take a lighter deload week now?"}
          </p>
        </div>
        <div className="grid gap-2">
          <Button type="button" disabled={busy} onClick={() => onHardAnswer(true)} className="h-11 rounded-xl font-bold">
            Yes, deload this week
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onHardAnswer(false)}
            className="h-11 rounded-xl border-border bg-muted/40 font-semibold hover:bg-muted/60"
          >
            Not now
          </Button>
        </div>
      </section>
    );
  }

  const { summary, deloadSoon, week } = suggestion;
  const missedDays = summary.planned > summary.completed;
  const choose = (answer) => {
    if (deloadSoon && (answer === "travelling" || answer === "busy")) {
      setPending({ answer, step: "deload" });
      return;
    }
    if (missedDays && (answer === "ill" || answer === "travelling")) {
      setPending({ answer, step: "repeat" });
      return;
    }
    onLightAnswer(answer, { countAsDeload: false });
  };

  return (
    <section
      aria-label="Weekly check-in"
      className="grid gap-3 rounded-2xl border border-border bg-card p-4"
    >
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
        <MessageCircleQuestion className="h-3.5 w-3.5" /> Check-in · week {week}
      </p>
      {pending?.step === "repeat" ? (
        <>
          <div>
            <h3 className="text-lg font-bold leading-tight text-white">Repeat last week?</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              Do week {week}'s missed sessions this week instead, and move the rest of the programme
              a week later. Or carry on with this week as planned.
            </p>
          </div>
          <div className="grid gap-2">
            <Button
              type="button"
              disabled={busy}
              onClick={() => onLightAnswer(pending.answer, { repeatWeek: true })}
              className="h-11 rounded-xl font-bold"
            >
              Yes, repeat it
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onLightAnswer(pending.answer, { countAsDeload: false })}
              className="h-11 rounded-xl border-border bg-muted/40 font-semibold hover:bg-muted/60"
            >
              No, carry on
            </Button>
          </div>
        </>
      ) : pending ? (
        <>
          <div>
            <h3 className="text-lg font-bold leading-tight text-white">Count it as your deload?</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              Your lighter deload week is due in week {deloadSoon}. If last week's break counts as
              the rest, week {deloadSoon} becomes a normal week.
            </p>
          </div>
          <div className="grid gap-2">
            <Button
              type="button"
              disabled={busy}
              onClick={() => onLightAnswer(pending.answer, { countAsDeload: true })}
              className="h-11 rounded-xl font-bold"
            >
              Yes, count it
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onLightAnswer(pending.answer, { countAsDeload: false })}
              className="h-11 rounded-xl border-border bg-muted/40 font-semibold hover:bg-muted/60"
            >
              No, keep my deload
            </Button>
          </div>
        </>
      ) : (
        <>
          <div>
            <h3 className="text-lg font-bold leading-tight text-white">Last week was light</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              You did {describeWeek(summary)}. What happened?
            </p>
          </div>
          <div className="grid gap-2" role="group" aria-label="What happened last week">
            {LIGHT_ANSWERS.map(({ answer, label }) => (
              <Button
                key={answer}
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => choose(answer)}
                className="h-11 justify-start rounded-xl border-border bg-muted/40 px-4 font-semibold hover:bg-muted/60"
              >
                {label}
              </Button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default ProgrammeCheckInCard;
